import React, { useEffect, useState } from 'react';
import {
  Box,
  MobileStepper,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { size } from 'lodash';

import { FromStepperProvider } from './context';
import { Props } from './props';

const FormStepperView: React.FC<Props> = (props) => {
  const {
    steps,
    children,
    stepProps,
    defaultStep,
    renderMobileTitle,
    stepperProps,
    stepLabelProps,
    labelColumnAlign = false,
    enableTitleOnMobile = true,
    mobileStepperProps,
    mobileProps,
    ...rest
  } = props;

  // ======================= HOOKS
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));

  // ======================= STATE
  const [activeStep, setActiveStep] = useState(0);
  const [data, setData] = useState<Record<string, any>>({});

  // ======================= EVENTS
  const onHandleNext = () =>
    setActiveStep((prevActiveStep) => prevActiveStep + 1);

  const onHandleBack = () => {
    if (activeStep < 1) return;
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onHandleReset = () => setActiveStep(0);

  const onHandleSaveData = (value: any) => {
    setData((prevData) => ({ ...prevData, ...value }));
  };

  const onResetData = () => {
    setData({});
  };
  // ======================= EFFECTS
  useEffect(() => {
    if (defaultStep) {
      setActiveStep(defaultStep);
    }
  }, [defaultStep, activeStep]);
  // ======================= RENDER FUNCTIONS
  const renderChildren = () => (
    <FromStepperProvider
      onHandleNext={onHandleNext}
      onHandleBack={onHandleBack}
      onHandleReset={onHandleReset}
      onHandleSaveData={onHandleSaveData}
      onResetData={onResetData}
      data={data}
    >
      {children(activeStep, { onHandleNext, onHandleBack, onHandleReset })}
    </FromStepperProvider>
  );

  const renderStepper = () => {
    if (isMobile) {
      const { mobileTitleProps, mobileLabelProps } = mobileProps || {};
      return (
        <React.Fragment>
          {enableTitleOnMobile && (
            <Typography
              fontSize={'.875rem'}
              className={'font-semibold'}
              {...mobileTitleProps}
            >
              {renderMobileTitle?.(activeStep) ?? `Step ${activeStep + 1}`}
            </Typography>
          )}
          {steps?.map((label, index) => (
            <StepLabel
              sx={{
                display: activeStep === index ? 'block' : 'none',
              }}
              {...stepLabelProps}
              {...mobileLabelProps}
              key={label}
            >
              <Typography>{label}</Typography>
            </StepLabel>
          ))}
          <MobileStepper
            LinearProgressProps={{
              sx: {
                width: '100%',
              },
            }}
            position="static"
            sx={{
              paddingInline: 0,
              paddingBottom: 7,
            }}
            variant="progress"
            backButton={null}
            nextButton={null}
            {...mobileStepperProps}
            steps={size(steps)}
            activeStep={activeStep}
          />
          {renderChildren()}
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        <Stepper
          {...stepperProps}
          activeStep={activeStep}
          alternativeLabel={labelColumnAlign}
        >
          {steps.map((label) => {
            return (
              <Step {...stepProps} key={label}>
                <StepLabel {...stepLabelProps}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
        {renderChildren()}
      </React.Fragment>
    );
  };

  // ======================= VIEWS
  return <Box {...rest}>{renderStepper()}</Box>;
};

export type FormStepperProps = Props;
export const FormStepper = FormStepperView;
export * from './hooks';
export default FormStepperView;
